import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Input,
  Table,
  Row,
  Col,
  Form,
  Badge,
  Collapse,
} from "reactstrap";
import { FormattedMessage } from "react-intl";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { injectIntl } from "react-intl";
import { GetFinalExamVariantsForCourse } from "../../actions/teacherQueries";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import { useQueryClient } from "react-query";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { faCaretDown, faCaretLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

const GenerateFinalExamQuestionsForm = ({
  classId,
  classStudents,
  course,
  studentCourseModules,
  student,
  level,
  dismissModal,
  intl,
}) => {
  const queryClient = useQueryClient();
  const animatedComponents = makeAnimated();
  const modulesToUse = useMemo(
    () =>
      course?.modules?.filter(
        (module) =>
          module.qApiId != null && !module.isFinalExam && module.isGy25
      ) || [],
    [course?.modules]
  );

  const getModuleId = (module) => module.moduleId || module.id;

  const getStudentModule = useCallback(
    (moduleId) => studentCourseModules.find((cm) => cm.moduleId === moduleId),
    [studentCourseModules]
  );

  const getMaxNumOfQuestions = useCallback(
    (module) => {
      const isStudent = level === "Student";
      const studentModule = isStudent ? getStudentModule(module.id) : null;

      if (
        module.hasQApiFetchErrselectedModulesor ||
        studentModule?.hasQApiFetchError
      )
        return "E";

      const maxNumber = isStudent
        ? studentModule?.numberOfQuestions
        : module.numberOfQuestions;

      return maxNumber != null ? Number(maxNumber) : "";
    },
    [getStudentModule, level]
  );

  const getDefaultNumOfQuestions = useCallback(
    (module, maxValue) => {
      const isStudent = level === "Student";
      const studentModule = isStudent ? getStudentModule(module.id) : null;

      const defaultValue = isStudent
        ? studentModule?.defaultNumberOfQuestionsInFinalExam
        : module.defaultNumberOfQuestionsInFinalExam;

      if (
        defaultValue == null ||
        (typeof maxValue === "number" &&
          !Number.isNaN(maxValue) &&
          defaultValue > maxValue)
      ) {
        return "";
      }

      return defaultValue;
    },
    [getStudentModule, level]
  );

  const modulesWithValidQues = useMemo(
    () =>
      modulesToUse.filter((module) => {
        const numQues =
          level === "Student"
            ? getStudentModule(module.id)?.numberOfQuestions
            : module.numberOfQuestions;
        return Number(numQues) > 0;
      }),
    [modulesToUse, level, getStudentModule]
  );

  const [variantName, setVariantName] = useState("");
  const [numAlt, setNumAlt] = useState(false);
  const [studentsCanRun, setStudentsCanRun] = useState(false);
  const [approvalLimit, setApprovalLimit] = useState(70);
  const [nameAlreadyInUse, setNameAlreadyInUse] = useState(false);
  const [nameEmptyError, setNameEmptyError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionCountErrors, setQuestionCountErrors] = useState({});
  const [selectedStudents, setSelectedStudents] = useState(
    level === "Student" ? [{ name: student[0].name, id: student[0].id }] : []
  );
  const [allModulesSelected, setAllModulesSelected] = useState(true);
  const [selectedModules, setSelectedModules] = useState(() => {
    return (
      modulesWithValidQues.map((module) => {
        const maxValue = getMaxNumOfQuestions(module);
        return {
          moduleId: getModuleId(module),
          checked: true,
          questionCount: getDefaultNumOfQuestions(module, maxValue),
          maxQuesCount: maxValue,
        };
      }) || []
    );
  });
  const [form, setForm] = useState("new");
  const [parentExamId, setParentExamId] = useState(null);
  const [selectedParentExam, setSelectedParentExam] = useState(null);
  const [isRetestExpanded, setIsRetestExpanded] = useState(false);

  const finalExamVariants = GetFinalExamVariantsForCourse(classId, course?.id);
  const variantsNames =
    finalExamVariants?.data?.map((ex) => ex.name.toLowerCase()) || [];
  const parentExams =
    finalExamVariants?.data
      ?.filter((ex) => !ex.isChildExam && ex.parentExamId == null)
      .sort((a, b) => new Date(b.created) - new Date(a.created)) || [];

  const mappedStudentOptions = (students) =>
    students
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((student) => ({
        name: student.name,
        id: student.id ?? student.studentId,
      }));

  const studentOptions =
    level === "Student"
      ? [{ name: student[0].name, id: student[0].id }]
      : [
          { name: intl.formatMessage({ id: "SelectAll" }), id: 0 },
          ...mappedStudentOptions(classStudents || []),
        ];

  const handleSingleModuleSelect = (moduleId) => {
    var updatedModules = [...selectedModules];

    const existingIndex = updatedModules.findIndex(
      (module) => getModuleId(module) === moduleId
    );

    if (existingIndex > -1) {
      updatedModules[existingIndex] = {
        ...updatedModules[existingIndex],
        checked: !updatedModules[existingIndex].checked,
      };
    } else {
      updatedModules.push({
        moduleId: moduleId,
        questionCount: selectedModules.find((m) => getModuleId(m) === moduleId)
          ?.questionCount,
        checked: true,
        maxQuesCount: selectedModules.find((m) => getModuleId(m) === moduleId)
          ?.maxQuesCount,
      });
    }

    setSelectedModules(updatedModules);
  };

  const handleAllModulesSelect = (e) => {
    if (modulesWithValidQues.length === 0) return;

    const allSelected = e.target.checked;
    setAllModulesSelected(allSelected);

    const updatedModules = modulesWithValidQues.map((m) => {
      const existing = selectedModules.find(
        (s) => getModuleId(s) === getModuleId(m)
      );
      return {
        moduleId: getModuleId(m),
        questionCount:
          existing?.questionCount ??
          getDefaultNumOfQuestions(m, getMaxNumOfQuestions(m)),
        checked: allSelected,
        maxQuesCount: existing?.maxQuesCount ?? getMaxNumOfQuestions(m),
      };
    });
    setSelectedModules(updatedModules);
  };

  const handleVariantNameChange = (e) => {
    const value = e.target.value;
    setVariantName(value);
    setNameEmptyError(false);

    if (value.trim() && variantsNames.includes(value.trim().toLowerCase())) {
      setNameAlreadyInUse(true);
    } else {
      setNameAlreadyInUse(false);
    }
  };

  const validateInput = (value, maxValue) => {
    if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
      return "EnterValidNumber";
    }
    if (maxValue != null && maxValue !== "" && value > maxValue) {
      return "EnterSmallerNumber";
    }
    return null;
  };

  const handleQuestionCountChange = (moduleId, maxValue, inputValue) => {
    const value = Number(inputValue);

    const hasError = validateInput(value, maxValue);

    var updatedModules = selectedModules.map((module) =>
      getModuleId(module) === moduleId
        ? { ...module, questionCount: Number(inputValue) }
        : module
    );
    setSelectedModules(updatedModules);

    setQuestionCountErrors((prevErrors) => {
      if (hasError) {
        return { ...prevErrors, [moduleId]: hasError };
      } else {
        const { [moduleId]: _, ...otherErrors } = prevErrors;
        return otherErrors;
      }
    });
  };

  const handleStudentsSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedStudents([]);
      return;
    }

    const isSelectAll = selectedOptions.some((opt) => opt.id === 0);

    const updatedStudents = isSelectAll
      ? mappedStudentOptions(classStudents)
      : selectedOptions.map((opt) => ({ id: opt.id, name: opt.name }));

    setSelectedStudents(updatedStudents);
  };

  const renderQuestionCountInput = (module, selectedModule) => {
    const maxQuestions = getMaxNumOfQuestions(module);
    const defaultCount = getDefaultNumOfQuestions(module, maxQuestions);
    const value = selectedModule.questionCount || defaultCount;
    const errorMessage = questionCountErrors[getModuleId(module)];

    return (
      <div className="d-flex align-items-center">
        <Input
          type="number"
          min="1"
          max={maxQuestions}
          value={value}
          onChange={(e) =>
            handleQuestionCountChange(
              getModuleId(module),
              maxQuestions,
              e.target.value
            )
          }
          style={{ width: "80px", textAlign: "right" }}
          disabled={maxQuestions === 0}
          className={`number-input ${errorMessage ? "input-invalid" : ""}`}
          title={errorMessage ? intl.formatMessage({ id: errorMessage }) : ""}
        />
        {maxQuestions !== "" && (
          <>
            <span className="me-1">/</span>
            <span
              title={
                maxQuestions === "E" &&
                intl.formatMessage({ id: "CouldNotGetMaxValue" })
              }
              className={maxQuestions === "E" && "text-danger input-feedback"}
            >
              {maxQuestions}
            </span>
          </>
        )}
      </div>
    );
  };

  const validateQuesInputBeforeSubmit = (selectedModules) => {
    const newErrors = {};

    selectedModules.forEach((m) => {
      if (!m.checked) return;

      const error = validateInput(Number(m.questionCount), m.maxQuesCount);

      if (error) {
        newErrors[m.moduleId] = error;
        toast.error(<FormattedMessage id={error} />, TOAST_CONFIG);
      }
    });

    setQuestionCountErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const getErrorMessage = (err) => {
    if (err?.response?.status === 500) {
      return <FormattedMessage id="GenericApiError" />;
    }

    const errorMessage = err?.response?.data?.trim() || err?.request?.data;

    if (errorMessage === "NameAlreadyInUse") {
      return <FormattedMessage id="NameAlreadyInUse" />;
    }

    if (errorMessage) return errorMessage;

    return <FormattedMessage id="QuestionGenerationFail" />;
  };

  const handleGenerateQuestions = async () => {
    const trimmedName = variantName.trim();

    if (selectedModules.every((m) => !m.checked)) {
      toast.warn(<FormattedMessage id="SelectModuleWarning" />, TOAST_CONFIG);
      return;
    }

    if (trimmedName.length === 0) {
      setNameEmptyError(true);
      return;
    }

    if (variantsNames.includes(trimmedName.toLowerCase())) {
      setNameAlreadyInUse(true);
      return;
    }
    if (!validateQuesInputBeforeSubmit(selectedModules)) return;

    setIsSubmitting(true);

    const courseId = course.id;
    const formData = {
      courseId: courseId,
      classId: classId,
      studentsCanRun,
      name: trimmedName,
      includedModules: selectedModules
        .filter((module) => module.checked)
        .map(({ moduleId, questionCount }) => ({ moduleId, questionCount })),
      forStudentIds: selectedStudents.map((student) => student.id),
      numAlt: numAlt,
      approvalLimit: parseInt(approvalLimit),
      parentId: parentExamId ? parentExamId : null,
    };

    const res = await axios
      .post("/Course/CreateFinalExamVariant", formData, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        toast.success(
          <FormattedMessage id="QuestionGenerationSuccess" />,
          TOAST_CONFIG
        );
        dismissModal();
        setIsSubmitting(false);
        queryClient.invalidateQueries([
          "FetchFinalExamVariantsForCourse",
          classId,
          courseId,
        ]);
      })
      .catch((err) => {
        const toastMessage = getErrorMessage(err);
        toast.warn(toastMessage, TOAST_CONFIG);
        setIsSubmitting(false);
      });

    return res;
  };

  const checkedModules = selectedModules.filter((s) => s.checked);

  const totalNumQuestions = checkedModules.reduce(
    (sum, s) => sum + (s.questionCount || 0),
    0
  );

  useEffect(() => {
    if (!selectedParentExam) return;

    const parentEx = {};

    selectedParentExam.content.forEach((c) => {
      parentEx[c.moduleId] = c.questionCount;
    });

    const mappedModules = modulesWithValidQues.map((m) => {
      const moduleId = getModuleId(m);
      const parentItem = selectedParentExam.content.find(
        (c) => c.moduleId === moduleId
      );

      return {
        moduleId,
        checked: !!parentItem,
        questionCount: parentItem
          ? parentItem.questionCount
          : getDefaultNumOfQuestions(m, getMaxNumOfQuestions(m)),
        maxQuesCount: getMaxNumOfQuestions(m),
      };
    });

    setSelectedModules(mappedModules);
    setAllModulesSelected(false);
    setNumAlt(selectedParentExam.numAlt);
    setVariantName(selectedParentExam.proposedNameNextChildExam);
    setSelectedStudents(selectedParentExam.proposedStudentsNextChildExam);

    if (selectedParentExam.approvalLimit != null) {
      setApprovalLimit(selectedParentExam.approvalLimit);
    }
  }, [selectedParentExam, modulesWithValidQues, getDefaultNumOfQuestions, getMaxNumOfQuestions]);

  const handleSwithToCreateNew = () => {
    setForm("new");
    setIsRetestExpanded(false);
    setParentExamId(null);
    setSelectedParentExam(null);

    const resetModules = modulesWithValidQues.map((module) => {
      const maxValue = getMaxNumOfQuestions(module);
      return {
        moduleId: getModuleId(module),
        checked: true,
        questionCount: getDefaultNumOfQuestions(module, maxValue),
        maxQuesCount: maxValue,
      };
    });

    setSelectedModules(resetModules);
    setAllModulesSelected(true);
    setNumAlt(false);
    setVariantName("");
    setSelectedStudents(
      level === "Student" ? [{ name: student[0].name, id: student[0].id }] : []
    );
    setApprovalLimit(70);
  };

  const handleSwithToCreateRetest = () => {
    setForm("retest");
    setIsRetestExpanded(false);
    setNameAlreadyInUse(false);
    setNameEmptyError(false);
  };

  const handleParentExamSelect = (opt) => {
    setParentExamId(opt.value);
    setIsRetestExpanded(true);
    setSelectedParentExam(parentExams.find((p) => p.id === opt.value));
  };

  if (finalExamVariants.isLoading) return <Pacmanloader />;

  return (
    <Form className="m-1">
      <div className="form-toggle-container mb-4">
        <label
          className={`form-option ${form === "new" ? "active" : ""}`}
          onClick={handleSwithToCreateNew}
        >
          <input
            type="radio"
            value="new"
            checked={form === "new"}
            onChange={() => setForm("new")}
          />
          <FormattedMessage id="NewExam" />
        </label>

        <label
          className={`form-option ${form === "retest" ? "active" : ""}`}
          onClick={handleSwithToCreateRetest}
        >
          <input
            type="radio"
            value="retest"
            checked={form === "retest"}
            onChange={() => setForm("retest")}
          />
          <FormattedMessage id="Retest" />
        </label>
      </div>

      {form === "retest" && (
        <div className="d-flex align-items-center mb-3">
          <label
            className="form-label fw-bold me-4"
            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
            onClick={() => {
              if (!parentExamId) return;
              setIsRetestExpanded((prev) => !prev);
            }}
          >
            <FontAwesomeIcon
              className={
                isRetestExpanded
                  ? "fa-caret--animated me-2"
                  : "fa-caret--animated me-2 collapsed"
              }
              icon={isRetestExpanded ? faCaretDown : faCaretLeft}
            />

            <FormattedMessage id="FromExam" />
          </label>

          <Select
            className="w-100"
            classNamePrefix="custom-select"
            closeMenuOnSelect
            components={animatedComponents}
            name="parentExams"
            options={parentExams.map((ex) => ({
              value: ex.id,
              label: (
                <>
                  {ex.name} - ({moment(ex.created).format("YYYY-MM-DD")})
                </>
              ),
            }))}
            onChange={(opt) => handleParentExamSelect(opt)}
            placeholder={<FormattedMessage id="SelectExam" />}
          />
        </div>
      )}

      <Collapse
        isOpen={form === "new" || (form === "retest" && isRetestExpanded)}
      >
        <div className="d-flex align-items-start mb-3">
          <label
            className="fw-bold ms-3 me-3 pt-2"
            style={{ whiteSpace: "nowrap" }}
          >
            <FormattedMessage id="Name" />
            <span className="text-danger">*</span>
          </label>

          <div className="flex-grow-1 d-flex flex-column ms-4">
            <Input
              id="variantName"
              className="rounded w-100"
              required
              value={variantName}
              onChange={handleVariantNameChange}
            />

            {(nameAlreadyInUse || nameEmptyError) && (
              <div
                className="text-danger"
                style={{ fontSize: "0.9em", marginTop: "2px", width: "100%" }}
              >
                {nameAlreadyInUse ? (
                  <FormattedMessage id="NameAlreadyInUse" />
                ) : (
                  <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.RequireName" />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="table-container">
          <Table className="module-ques-head">
            <thead className="table-light">
              <tr>
                <th>
                  <Input
                    type="checkbox"
                    style={{ cursor: "pointer" }}
                    className="ms-2"
                    checked={allModulesSelected}
                    onChange={(e) => handleAllModulesSelect(e)}
                  />
                </th>
                <th>
                  <FormattedMessage id="Module" />
                </th>
                <th>
                  <FormattedMessage id="QuestionCount" /> / Max
                </th>
                <th></th>
              </tr>
            </thead>
          </Table>

          <div className="module-ques-body-wrapper mt-3">
            <Table className="module-ques-body">
              <tbody>
                {modulesToUse?.length > 0 ? (
                  [...modulesToUse]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((module) => {
                      const moduleId = getModuleId(module);
                      const selectedModule =
                        selectedModules.find((m) => m.moduleId === moduleId) ||
                        {};
                      const studentModule =
                        level === "Student"
                          ? getStudentModule(module.id)
                          : null;

                      const numQuestions =
                        level === "Student"
                          ? Number(
                              studentModule?.defaultNumberOfQuestionsInFinalExam
                            )
                          : Number(module.numberOfQuestions);

                      const isDisabled = numQuestions === 0;
                      const isChecked = isDisabled
                        ? false
                        : selectedModule.checked ?? false;

                      return (
                        <tr key={moduleId}>
                          <td>
                            <Input
                              type="checkbox"
                              style={{
                                cursor: "pointer",
                                opacity: isDisabled ? 0.3 : 1,
                              }}
                              className="ms-2"
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={() =>
                                handleSingleModuleSelect(moduleId)
                              }
                            />
                          </td>
                          <td>
                            {level === "Student"
                              ? studentModule.nameCorrespondingFactModule
                              : module.nameCorrespondingFactModule}
                          </td>
                          <td>
                            {renderQuestionCountInput(module, selectedModule)}
                          </td>
                          <td></td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan="4" style={{ border: "none" }}>
                      <FormattedMessage id="NoAvailableModules" />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
        {modulesToUse.length > 0 && (
          <>
            <div className="p-2 bg-white rounded shadow-sm mt-3">
              <Row className="align-items-center mb-3">
                <Col xs="auto">
                  <span className="me-2">
                    <FormattedMessage id="SelectedModules" />:
                  </span>
                  <Badge color="info" className="rounded-pill px-2">
                    {checkedModules.length} / {modulesToUse.length}
                  </Badge>
                </Col>
                <Col>
                  <span className="me-2">
                    <FormattedMessage id="TotalNumQuestions" />:
                  </span>
                  <Badge color="info" className="rounded-pill px-2">
                    {totalNumQuestions}
                  </Badge>
                </Col>
              </Row>
              <Row className="align-items-center mb-3">
                <Col xs="auto" style={{ width: "80px" }}>
                  <FormattedMessage id="Student" />
                </Col>
                <Col>
                  <Select
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    isMulti
                    name="students"
                    options={studentOptions}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.name}
                    onChange={handleStudentsSelect}
                    value={
                      selectedStudents
                        ? mappedStudentOptions(selectedStudents)
                        : []
                    }
                    placeholder={<FormattedMessage id="ChooseStudents" />}
                  />
                </Col>
              </Row>
              <Row className="align-items-center mb-3">
                <Col xs="auto">
                  <FormattedMessage id="ApproveScoreLimitInPercentage" />
                </Col>
                <Col>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    name="approvalLimit"
                    className="form-control rounded w-100"
                    value={approvalLimit}
                    onChange={(e) => setApprovalLimit(e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="align-items-center mb-3 ml-1">
                <Col xs="auto">
                  <Input
                    name="numAlt"
                    style={{ cursor: "pointer", marginRight: "5px" }}
                    type="checkbox"
                    checked={numAlt}
                    onChange={(e) => setNumAlt(e.target.checked)}
                  />
                  <FormattedMessage id="ShowAnswerOptions" />
                </Col>
              </Row>
              <Row className="align-items-center mb-3 ml-1">
                <Col xs="auto">
                  <Input
                    name="studentsCanRun"
                    style={{ cursor: "pointer", marginRight: "5px" }}
                    type="checkbox"
                    checked={studentsCanRun}
                    onChange={(e) => setStudentsCanRun(e.target.checked)}
                  />
                  <FormattedMessage id="StudentsCanRun" />
                </Col>
              </Row>
            </div>

            <div className="d-flex flex-row justify-content-end mt-4 me-1">
              <Button
                outline
                color="secondary"
                onClick={dismissModal}
                className="px-3 py-1 me-2"
              >
                <FormattedMessage id="Cancel" />
              </Button>

              <Button
                outline
                color="primary"
                disabled={isSubmitting}
                onClick={handleGenerateQuestions}
                className="px-3 py-1"
              >
                {isSubmitting ? (
                  <FormattedMessage id="Wait" />
                ) : (
                  <FormattedMessage id="SaveAndClose" />
                )}
              </Button>
            </div>
          </>
        )}
      </Collapse>
    </Form>
  );
};

export default injectIntl(GenerateFinalExamQuestionsForm);
